import {
  Component,
  OnInit,
  Input,
  Inject,
  ViewChild,
  ElementRef,
} from "@angular/core";
import {
  FormControl,
  Validators,
  FormBuilder,
  FormGroup,
} from "@angular/forms";
import { CookieService } from "ngx-cookie-service";
import { VolvoApiService } from "../../../../services/volvo-api.service";
import { MatDialogRef } from "@angular/material";
import { FormControlService } from "../../../../services/form-control.service";
import { Observable, throwError } from "rxjs";
import { FormFieldDropdown } from "../../../../models/form-field-dropdown";
import { FormFieldTextBox } from "../../../../models/form-field-textbox";
import { PageConfiguration } from "../../../../models/page-configuration";

@Component({
  selector: "app-registration",
  templateUrl: "./registration.component.html",
  styleUrls: ["./registration.component.scss"],
})
export class RegistrationComponent implements OnInit {
  @Input() Guid: string;
  @Input() AdditionalFieldsFormID: number;
  @Input() CountryID: number;
  @Input() localStrings: Array<any>;
  @Input() pageConfig: PageConfiguration;
  @Input() promotionAlias: string;
  @Input() isHotelPromotion: boolean;

  regForm: FormGroup;
  offerFormRowFields = [];
  private extraFields = [];
  private extraFormRowFields: Array<any>;
  private partners: Observable<any>;
  private states: Observable<any>;
  private partnerDropdown: FormFieldDropdown;
  private stateDropdown: FormFieldDropdown;
  private driversLicenseNumberField: FormFieldTextBox;
  private issuanceStateDropdown: FormFieldDropdown;
  errorMessages: Array<any>;
  public formSubmitAttempt: boolean;
  public termsExpanded: boolean = false;
  urlBase: string;
  submitSuccess = false;

  showPreferredDealers = false;
  loadingDealers = false;
  noDealersFound = false;
  nearbyDealers: any = [];
  nearbyDealersLimit = 3;

  constructor(
    private volvoApi: VolvoApiService,
    private dialogRef: MatDialogRef<RegistrationComponent>,
    private _fb: FormBuilder,
    private _fs: FormControlService,
    private cookieService: CookieService
  ) {}

  ngOnInit() {
    this.urlBase = this.urlBase = `${this.isHotelPromotion ? "/hotel" : ""}/${
      this.promotionAlias
    }`;

    // create form
    const ls = this.localStrings;

    // partner dropdown
    const partnerValidationText =
      this.pageConfig.PartnerCodeSelectValidationText != ""
        ? this.pageConfig.PartnerCodeSelectValidationText
        : ls["AV.Promotions.lb.validation.partnerCode"];
    this.partnerDropdown = this._fs.createDropDownField(
      "partnerCode",
      null,
      null,
      Validators.required,
      partnerValidationText,
      partnerValidationText
    );
    this.offerFormRowFields.push([this.partnerDropdown]);

    // add the fleet company and
    // fleet VIN fields to the form
    if(this.pageConfig.Format === `fleet`) {

      Array.prototype.push.apply(
        this.offerFormRowFields,
        this._fs.getFleetPinFields(ls)
      );

    }

    // add the red sox fields to the form
    if(this.pageConfig.Format === `redsox`) {

      Array.prototype.push.apply(
        this.offerFormRowFields,
        this._fs.getRedSoxPinFields(ls)
      );

    }

    // add the basic infor fields
    Array.prototype.push.apply(
      this.offerFormRowFields,
      this._fs.getBasicInfoFields(ls, null, this.pageConfig.Format)
    );

    // state and zip code
    this.stateDropdown = this._fs.createDropDownField(
      "state",
      "column medium-6 form-control-wrapper",
      null,
      Validators.required,
      ls["AV.Promotions.lb.stateID"],
      ls["AV.Promotions.lb.stateID"]
    );
    const zipCodeField = this._fs.createTextBox(
      "zipcode",
      ls["AV.Promotions.lb.zipcode"],
      null,
      "column medium-6 form-control-wrapper",
      null,
      ls["AV.Promotions.lb.validation.zipcode"],
      ls["AV.Promotions.lb.validation.zipcode.invalid"],
      [
        Validators.required,
        this._fs.patternValidator(
          /(^[abceghjklmnprstvxyABCEGHJKLMNPRSTVXY]\d[abceghjklmnprstvwxyzABCEGHJKLMNPRSTVWXYZ]( )?\d[abceghjklmnprstvwxyzABCEGHJKLMNPRSTVWXYZ]\d$)|(^\d{5}(-\d{4})?$)/
        ),
      ]
    );
    this.offerFormRowFields.push([this.stateDropdown, zipCodeField]);

    // email and phone number
    const emailField = this._fs.createTextBox(
      "email",
      ls["AV.Promotions.lb.emailAddress"],
      null,
      "column medium-6 form-control-wrapper",
      null,
      ls["AV.Promotions.lb.validation.fromAddress"],
      ls["AV.Promotions.lb.validation.emailAddress.invalid"],
      [Validators.required, Validators.email]
    );
    const phoneField = this._fs.createTextBox(
      "phone",
      ls["AV.Promotions.lb.phoneNumber"],
      null,
      "column medium-6 form-control-wrapper",
      null,
      ls["AV.Promotions.lb.validation.phoneNumber"],
      ls["AV.Promotions.lb.validation.phoneNumber.invalid"],
      [
        Validators.required,
        this._fs.patternValidator(
          /^(1[-. ]?)?(\([2-9]\d{2}\)|[2-9]\d{2})[-. ]?[2-9]\d{2}[-. ]?\d{4}(([xX]|ext|EXT)?\d{1,5})?$/
        ),
      ]
    );
    this.offerFormRowFields.push([emailField, phoneField]);

    // drivers license number and issuance state,
    // only shown when the promotion is configured for it
    if (this.pageConfig.PromotionShowDriversLicense) {
      this.driversLicenseNumberField = this._fs.createTextBox(
        "driversLicenseNumber",
        ls["AV.Promotions.lb.driversLicenseNumber"],
        null,
        "column medium-6 form-control-wrapper",
        null,
        ls["AV.Promotions.lb.validation.fromDriversLicenseNumber"],
        null,
        [Validators.required]
      );
      this.issuanceStateDropdown = this._fs.createDropDownField(
        "issuanceState",
        "column medium-6 form-control-wrapper",
        null,
        Validators.required,
        ls["AV.Promotions.lb.issuanceState"],
        ls["AV.Promotions.lb.issuanceState"]
      );
      this.offerFormRowFields.push([
        this.driversLicenseNumberField,
        this.issuanceStateDropdown,
      ]);
    }

    const pinTypes = [{
      key: '',
      value: ls["AV.Promotions.lb.programOption.required"],
      selected: true,
      disabled: true
    }];
    this.pageConfig.ProgramType.forEach(item => {
      pinTypes.push({
        key: item.ProgramTypeCode,
        value: item.ProgramTypeDescription,
        selected: false,
        disabled: false
      });
    });

    const pinTypeDropdown = this._fs.createDropDownField(
      "programOption",
      "",
      pinTypes,
      Validators.required,
      ls["AV.Promotions.lb.programOption.required"],
      ls["AV.Promotions.lb.programOption"],
      ls["AV.Promotions.lb.programOptionDescription"]);
    this.offerFormRowFields.push([pinTypeDropdown]);

    if (this.pageConfig.PromotionHidePrefContact == false) {
      // preferred method
      const methodOptions = [
        {
          label: ls["AV.Promotions.lb.contactMethodPhone"],
          value: "phone",
          checked: false,
        },
        {
          label: ls["AV.Promotions.lb.contactMethodEmail"],
          value: "email",
          checked: false,
        },
        // {
        //   label: ls["AV.Promotions.lb.contactMethodSMS"],
        //   value: "sms",
        //   checked: false,
        // },
        {
          label: ls["AV.Promotions.lb.contactMethodDoNotContact"],
          value: "doNotContact",
          checked: false,
        },
      ];
      const preferredMethodField = this._fs.createRadioField(
        "preferredContactMethod",
        ls["AV.Promotions.lb.contactMethod"],
        "",
        null,
        methodOptions,
        Validators.required,
        ls["AV.Promotions.lb.contactMethod.required"],
        this.pageConfig.PromotionPreferredContactText
      );
      this.offerFormRowFields.push([preferredMethodField]);
    }

    // load additional fields
    if (this.AdditionalFieldsFormID != 0) {
      this.volvoApi.getAdditionalFields("en-US", this.Guid).subscribe(
        (r) => {
          this.extraFormRowFields = this._fs.generateFormRows(r, true);
          this.extraFields.push({
            Name: r.form.field[0].column[0],
          });
          Array.prototype.push.apply(
            this.offerFormRowFields,
            this.extraFormRowFields
          );
          this.createForm();
        },
        (e) => console.error(e)
      );
    } else {
      this.createForm();
    }

    // load partners dropdown list
    this.partners = this.volvoApi.getAvailablePartners(this.Guid);
    this.partners.subscribe((r) => {
      const selectLabel = this.pageConfig.PartnerCodeSelectText
        ? this.pageConfig.PartnerCodeSelectText
        : ls["AV.Promotions.lb.partnerSelectText"];

      this.partnerDropdown.options = this._fs.createOptionsObj(
        r,
        "Code",
        selectLabel
      );

      // select and hide dropdown if there is only one partner
      if (r.length == 1) {
        this.partnerDropdown.options[1].selected = true;
        this.regForm.controls["partnerCode"].setValue(
          this.partnerDropdown.options[1].key
        );
        this.partnerDropdown.cssClasses += " hide";
      }
    });

    // load states dropdown list
    this.states = this.volvoApi.getStates(this.CountryID);
    this.states.subscribe((r) => {
      this.stateDropdown.options = this._fs.createOptionsObj(
        r,
        "Id",
        ls["AV.Promotions.lb.stateID"]
      );
      if (this.issuanceStateDropdown) {
        this.issuanceStateDropdown.options = this._fs.createOptionsObj(
          r,
          "Id",
          ls["AV.Promotions.lb.issuanceState"]
        );
      }
    });
  }

  get optin() {
    return this.regForm.get("optin");
  }
  get agreeterm() {
    return this.regForm.get("agreeterm");
  }

  get agreeterm_optin() {
    return this.regForm.get("agreeterm_optin");
  }
  get preferredDealerID() {
    return this.regForm.get("preferredDealerID");
  }

  createForm() {
    this.regForm = this._fs.toFormGroup(this.offerFormRowFields);

    // register static form control fields
    this.regForm.addControl(
      "optin",
      new FormControl("", Validators.nullValidator)
    );
    this.regForm.addControl(
      "agreeterm",
      new FormControl("", Validators.requiredTrue)
    );
    this.regForm.addControl(
      "agreeterm_optin",
      new FormControl("", Validators.requiredTrue)
    );
    this.regForm.addControl(
      "preferredDealerID",
      new FormControl("", Validators.required)
    );

    this.regForm.controls["optin"].setValue(false);

    const idMeInfo = this.cookieService.get("idMeInfo");

    if (this.pageConfig.UseIdMe && idMeInfo !== "") {
      try {
        const idMeInfo_obj = JSON.parse(idMeInfo);
        this.regForm.controls["firstname"].setValue(idMeInfo_obj.FirstName);
        this.regForm.controls["lastname"].setValue(idMeInfo_obj.LastName);
        this.regForm.controls["email"].setValue(idMeInfo_obj.Email);
        this.regForm.controls["zipcode"].setValue(idMeInfo_obj.Zip);
      } catch (error) {
        console.log(error);
        throwError(error);
      }
    }

    this.checkFormValues();
  }

  checkFormValues() {
    // if we have preferred contacts,
    // we need to wait and check
    if (this.pageConfig.PromotionHidePrefContact == false) {
      this.regForm.get("zipcode").valueChanges.subscribe((val) => {
        this.checkFieldsForNearestDealer();
      });

      this.regForm
        .get("preferredContactMethod")
        .valueChanges.subscribe((val) => {
          if (
            val === "doNotContact" ||
            (this.nearbyDealers.length === 0 &&
              !this.noDealersFound &&
              !this.loadingDealers)
          ) {
            this.checkFieldsForNearestDealer();
          }
        });
    } else {
      this.regForm.get("preferredDealerID").setValue(0);
    }
  }

  checkFieldsForNearestDealer() {
    const _inputContactMethod = this.regForm.get("preferredContactMethod");
    const addressZipValid = this.regForm.get("zipcode").valid;
    const contactMethodSelected =
      _inputContactMethod.value !== "" &&
      _inputContactMethod.value !== "doNotContact";

    this.nearbyDealers = [];
    this.showPreferredDealers = false;
    this.noDealersFound = false;
    this.regForm.get("preferredDealerID").setValue("");

    if (addressZipValid && contactMethodSelected) {
      this.nearestDealerOptions();
    } else {
      if (_inputContactMethod.value === "doNotContact") {
        this.regForm.get("preferredDealerID").setValue(0);
      }
    }
  }

  nearestDealerOptions() {
    // load states dropdown list
    this.showPreferredDealers = true;
    this.loadingDealers = true;

    let dealers = this.volvoApi.getNearestDealers(
      this.regForm.get("address").value,
      this.regForm.get("city").value,
      this.regForm.get("zipcode").value,
      this.CountryID
    );
    dealers.subscribe((r) => {
      if (r.length === 0) {
        this.noDealersFound = true;
      } else {
        this.nearbyDealers = r;
      }

      this.loadingDealers = false;
    });
  }

  selectNearbyDealer(e) {
    this.regForm.get("preferredDealerID").setValue(e.target.value);
  }

  showMoreRetailLocations(e) {
    this.nearbyDealersLimit = 6;
  }

  onSubmit() {
    // extra fields value
    this.extraFields.map((e) => {
      return (e["Value"] = this.regForm.controls[e.Name].value);
    });

    if (this.regForm.valid) {
      this.formSubmitAttempt = true;
      const formData = this.regForm.value;
      formData.promotionGuid = this.Guid;
      formData.culture = this.volvoApi.getCultureCode();

      if (formData.preferredContactMethod == undefined) {
        formData.preferredContactMethod = "";
      }

      formData.extraFields = this.extraFields;
      formData.externalID = "";
      formData.referenceID = "";

      // the webservice binds every declared parameter, so these
      // have to be sent even when the fields are not on the form
      if (!this.pageConfig.PromotionShowDriversLicense) {
        formData.driversLicenseNumber = "";
        formData.issuanceState = "";
      }

      const idMeInfo = this.cookieService.get("idMeInfo");

      if (this.pageConfig.UseIdMe && idMeInfo !== "") {
        try {
          const idMeInfo_obj = JSON.parse(idMeInfo);
          formData.externalID = idMeInfo_obj.UniqueIdentifier;
          formData.referenceID = idMeInfo_obj.ReferenceID;
        } catch (error) {
          console.log(error);
          throwError(error);
        }
      }

      let createPinRequest = null;

      if(this.pageConfig.Format === 'fleet') {
        // if the page format is `fleet`
        // then we need to create the pin using
        // a specific webservice method
        createPinRequest = this.volvoApi.CreateFleetPin(formData);
      }
      else if(this.pageConfig.Format === 'redsox') {
        // if the page format is `redsox`
        // then we need to create the pin using
        // a specific webservice method
        createPinRequest = this.volvoApi.CreateRedSoxPin(formData);
      }
      else {
        // otherwise lets create a normal PIN using
        // the existing CreatePin webservice
        createPinRequest = this.volvoApi.createPin(formData);
      }

      // we will subscribe to the request and
      // do something based on the response
      createPinRequest.subscribe((r) => {
        if (r.Success) {
          this.formSubmitAttempt = false;
          this.errorMessages = null;
          this.submitSuccess = true;
          setTimeout(() => {
            this.dialogRef.close();
          }, 15000);
        } else {
          this.formSubmitAttempt = false;
          this.errorMessages = r.ErrorMessages;
          console.log("error creating pin");
        }

        if (this.pageConfig.UseIdMe) {
          // delete the cookies so that the user
          // must reauthenicate w/ idme
          this.cookieService.delete("idMeInfo");
          this.cookieService.delete("idMeCode");
        }
      });

    }

  }

  isFieldValid(field: string) {
    return (
      (!this.regForm.get(field).valid &&
        (this.regForm.get(field).touched || this.regForm.get(field).dirty)) ||
      (this.regForm.get(field).untouched && this.formSubmitAttempt)
    );
  }

  clickTermsExpand() {
    this.termsExpanded = !this.termsExpanded;
  }

  clickSubmit() {
    if (this._fs.validateForm(this.regForm)) {
      this.onSubmit();
    }
  }
}
